import { last, omit } from 'lodash';
import { RouteLocationNormalizedLoaded, Router, useRoute, useRouter } from 'vue-router';
import type { Navigation, NavigationEntryRaw } from './types';
import { computed } from 'vue';
import { resolveRouteLocationWithPermissions } from '@/router/utils';

/**
 * Filters a given list of menuEntries, to make sure only those are shown, that are accessible (e.g. restrictions by route-permission)
 */
export const getFilteredNavigation = (
  router: Router,
  route: RouteLocationNormalizedLoaded,
  entries: NavigationEntryRaw[] = [],
): Navigation => {
  const targetEntries = entries
    // check if User can access the given route
    .filter((entry) => {
      return entry.route ? Boolean(resolveRouteLocationWithPermissions(entry.route, router)) : true;
    })
    .filter((entry) =>
      entry.route ? !last(router.resolve(entry.route).matched)?.meta.disabled : true,
    )
    // check if user has additional checks
    .filter((entry) => (entry.isAvailable ? entry.isAvailable() : true))
    .map((entry) => {
      const subNavigation = entry.children && getFilteredNavigation(router, route, entry.children);
      const isActive = route.matched.some(
        (matchedRoute) => matchedRoute.name === entry.route?.name,
      );

      return {
        ...omit(entry, ['children', 'isAvailable']),
        subNavigation,
        isActive,
      };
    })
    // check if there is a subNavigation with no entries, then we will ignore the parent
    .filter((entry) => {
      return entry.subNavigation?.entries.length !== 0;
    });

  return {
    entries: targetEntries,
    isActive: targetEntries?.some((childEntry) => childEntry.isActive),
  };
};

export function useReactiveFilteredNavigation(menu?: NavigationEntryRaw[]) {
  const router = useRouter();
  const route = useRoute();
  return computed(() => {
    return getFilteredNavigation(router, route, menu);
  });
}
